/**
 * Is a permission probe an actual VERDICT, or did we just never find out?
 *
 * `allowed: false` is the value for BOTH "the backend said no" and "the
 * entitlement lookup failed". Conflating them is how an administrator saw
 * *"Your role does not have access to this view"* on the demo while every
 * network call — including both `POST /auth/entitlements/evaluate` — returned
 * 200 and no 403 was issued anywhere. A view must never render a verdict the
 * backend did not state.
 *
 * Lives in its own module rather than inline in `use-list-archetype.tsx` so the
 * rule is testable on its own and so the archetype file keeps shrinking; see
 * `@tyndo/hooks`' `PermissionCheckResult.resolved` for the producer side.
 */

/** The shape `use-list-archetype`'s registered permission hook returns. */
export interface PermissionProbe {
  allowed: boolean;
  loading: boolean;
  /** Set when the entitlement lookup itself failed — never a denial. */
  error?: Error;
  /** True only on a real verdict. Optional: pre-`resolved` hooks still fit. */
  resolved?: boolean;
}

/**
 * True when `probe` carries a verdict the backend actually gave. The `??`
 * fallback keeps a custom hook registered before `resolved` existed working —
 * minus the error case, which is never a denial.
 */
export const isResolvedVerdict = (probe: PermissionProbe): boolean =>
  probe.resolved ?? (!probe.loading && probe.error === undefined);

/** Denial requires an affirmative NEGATIVE verdict, not merely `!allowed`. */
export const isDenied = (probe: PermissionProbe, gated: boolean): boolean =>
  gated && isResolvedVerdict(probe) && !probe.allowed;

/** An unresolved probe is pending — the view shows its loading affordance. */
export const isPending = (probe: PermissionProbe, gated: boolean): boolean =>
  gated && (probe.loading || !isResolvedVerdict(probe));
